import * as Yup from "yup";
import {
  isValidPhone,
  isValidEmail,
  isValidZip
} from "utils/redux-form";
import {
  isValidCustomReminderDays,
  isValidCustomPreorderDays,
  isValidTopicDuration
} from "./company-form-validators";

const validPhoneTest = (value, ctx) => {
  const err = isValidPhone(value);
  return err ? ctx.createError({ message: err }) : true;
};

const validZipTest = (value, ctx) => {
  const err = isValidZip(value);
  return err ? ctx.createError({ message: err }) : true;
};

const validEmailTest = (value, ctx) => {
  const err = isValidEmail(value);
  return err ? ctx.createError({ message: err }) : true;
};

const customReminderDaysTest = (value, ctx) => {
  const err = isValidCustomReminderDays(value);
  return err ? ctx.createError({ message: err }) : true;
};

const customPreorderDaysTest = (value, ctx) => {
  const err = isValidCustomPreorderDays(value);
  return err ? ctx.createError({ message: err }) : true;
};

const topicDurationTest = (value, ctx) => {
  const err = isValidTopicDuration(value);
  return err ? ctx.createError({ message: err }) : true;
};

// Account number is required only when uses_<x>_fulfillment is true AND not using branch-level fulfillment
const conditionalFulfillmentRequired = checkboxField =>
  Yup.string().when([checkboxField, "bUsesBranchFulfillment"], {
    is: (uses, branchLevel) => uses && !branchLevel,
    then: schema => schema.required("Required"),
    otherwise: schema => schema.notRequired()
  });

const companyFormValidationSchema = Yup.object().shape({
  company_name: Yup.string().required("Required."),
  account_number: Yup.string().required("Required."),
  wellsky_account_number: Yup.string().required("Required."),
  outbound_phone_number: Yup.string()
    .nullable()
    .test({ name: "validPhone", test: validPhoneTest }),
  reimbursement_multiplier: Yup.mixed().test(
    "required",
    "Required.",
    value => value !== "" && value !== null && value !== undefined
  ),
  team_name: Yup.string().required("Required."),
  pap_resupply_email_address: Yup.string()
    .required("Required.")
    .test({ name: "validEmail", test: validEmailTest }),

  settings: Yup.object().shape({
    sVoicemailCallbackNumber: Yup.string()
      .nullable()
      .test({ name: "validPhone", test: validPhoneTest }),
    iDaysAllowPreorder: Yup.mixed().nullable().test({
      name: "preorderDays",
      test: customPreorderDaysTest
    })
  }),

  // Conditional fulfillment account numbers
  cpap_account_number: conditionalFulfillmentRequired("uses_cpap_fulfillment"),
  independance_account_number: conditionalFulfillmentRequired(
    "uses_independence_fulfillment"
  ),
  internal_account_number: conditionalFulfillmentRequired(
    "uses_internal_fulfillment"
  ),
  mckesson_account_number: conditionalFulfillmentRequired(
    "uses_mckesson_fulfillment"
  ),
  ppm_account_number: conditionalFulfillmentRequired("uses_ppm_fulfillment"),
  vgm_account_number: conditionalFulfillmentRequired("uses_vgm_fulfillment"),

  address: Yup.object().shape({
    zip: Yup.string()
      .nullable()
      .test({ name: "validZip", test: validZipTest })
  }),

  // IVR fields - conditionally required when ivr_company is true
  ivr_start: Yup.string().when("ivr_company", {
    is: true,
    then: schema => schema.required("Required."),
    otherwise: schema => schema.notRequired()
  }),
  ivr_end: Yup.string()
    .when("ivr_company", {
      is: true,
      then: schema => schema.required("Required."),
      otherwise: schema => schema.notRequired()
    })
    .test("ivrEndAfterStart", "Invalid end time", function (value) {
      const { ivr_start } = this.parent;
      if (value && ivr_start && ivr_start > value) return false;
      return true;
    }),
  ivr_outbound_phone_number: Yup.string()
    .nullable()
    .test({ name: "validPhone", test: validPhoneTest })
    .when("ivr_company", {
      is: true,
      then: schema => schema.required("Required."),
      otherwise: schema => schema
    }),
  ivr_company_transfer_number: Yup.string()
    .nullable()
    .test({ name: "validPhone", test: validPhoneTest }),
  ivr_order_goal: Yup.mixed().when("ivr_company", {
    is: true,
    then: schema =>
      schema.test(
        "required",
        "Required.",
        value => value !== "" && value !== null && value !== undefined
      ),
    otherwise: schema => schema.notRequired()
  }),

  // Outreach end > start
  outreach_end_time: Yup.string().test(
    "outreachEndAfterStart",
    "Invalid end time",
    function (value) {
      const { outreach_start_time } = this.parent;
      if (value && outreach_start_time && outreach_start_time > value)
        return false;
      return true;
    }
  ),

  // Office hours end > start
  office_hours_end: Yup.string().test(
    "officeEndAfterStart",
    "Invalid end time",
    function (value) {
      const { office_hours_start } = this.parent;
      if (value && office_hours_start && office_hours_start > value)
        return false;
      return true;
    }
  ),

  customReminderDays: Yup.mixed().nullable().test({
    name: "reminderDays",
    test: customReminderDaysTest
  }),

  // Topic durations array
  topics_duration: Yup.array().of(
    Yup.object().shape({
      iMinutes: Yup.mixed().nullable().test({
        name: "topicDuration",
        test: topicDurationTest
      })
    })
  ),

  // Patient type settings array - phone numbers
  patient_type_settings: Yup.array().of(
    Yup.object().shape({
      phone_number: Yup.string()
        .nullable()
        .test({ name: "validPhone", test: validPhoneTest }),
      verified_phone_number: Yup.string()
        .nullable()
        .test({ name: "validPhone", test: validPhoneTest })
    })
  )
});

export default companyFormValidationSchema;
